
import { Service } from '../../types';
import { synczbase } from '../../lib/synczbase';

// Helper to remove undefined values and enforce types
const cleanServicePayload = (data: Partial<Service>, isUpdate: boolean = false): any => {
    let clean = JSON.parse(JSON.stringify(data));

    if (clean.price !== undefined) clean.price = Number(clean.price);
    if (isNaN(clean.price)) clean.price = 0;

    if (clean.durationMinutes !== undefined) clean.durationMinutes = Number(clean.durationMinutes);
    if (isNaN(clean.durationMinutes)) clean.durationMinutes = 0;

    if (clean.bufferMinutes !== undefined) clean.bufferMinutes = Number(clean.bufferMinutes);
    if (isNaN(clean.bufferMinutes)) clean.bufferMinutes = 0;

    if (clean.maxParticipants !== undefined) clean.maxParticipants = Number(clean.maxParticipants);
    if (isNaN(clean.maxParticipants)) clean.maxParticipants = 1;

    const type = clean.serviceType;

    if (type === 'Custom') {
        clean.isBookable = false;
        clean.bookingFlow = 'REQUEST';
        clean.durationMinutes = 0;
        clean.calendarId = null;
    } 
    else if (type === 'Workshop' || type === 'Event') {
        if (clean.isBookable === undefined && !isUpdate) clean.isBookable = true;
        if (clean.bookingFlow === undefined && !isUpdate) clean.bookingFlow = 'INSTANT';
        if ((clean.maxParticipants === undefined || clean.maxParticipants === 1) && !isUpdate) clean.maxParticipants = 10;
    } 
    else if (type === 'Project') {
        if (clean.isBookable === undefined && !isUpdate) clean.isBookable = true;
        if (clean.bookingFlow === undefined && !isUpdate) clean.bookingFlow = 'INSTANT';
        if ((clean.durationMinutes === 0 || clean.durationMinutes === undefined) && !isUpdate) clean.durationMinutes = 60;
    }

    if (!isUpdate) {
        const defaults: Partial<Service> = {
            title: 'Untitled Service',
            description: '',
            price: 0,
            currency: 'USD',
            enabled: true,
            features: [],
            portfolioId: null as any, 
            artistId: null as any,
            quoteFormType: 'NONE',
            isBookable: false,
            durationMinutes: 0,
            maxParticipants: 1,
            locationMode: 'PHYSICAL'
        };
        clean = { ...defaults, ...clean };
    }

    if (typeof clean.price !== 'number') clean.price = 0;
    if (typeof clean.title !== 'string') clean.title = 'Untitled';
    
    return clean;
};

export const getServices = async (): Promise<Service[]> => {
    try {
        const snap = await synczbase.query("SELECT * FROM services");
        return (snap || []).map((row: any) => {
            if (row.data) {
                try {
                    const parsed = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                    return { ...row, ...parsed, id: row.id || parsed.id };
                } catch (e) {}
            }
            return row;
        }) as Service[];
    } catch (e) {
        console.error("Failed to get services", e);
        return [];
    }
};

export const subscribeToServices = (callback: (services: Service[]) => void) => {
    // Real-time not perfectly emulated yet in synczbase query polling without sockets setup per-table
    // For now we just fetch once. A true synczbase subscribe requires table channel listening.
    getServices().then(callback).catch(console.error);
    return () => {}; // return empty unsubscribe
};

export const getServicesForPortfolio = async (portfolioId: string): Promise<Service[]> => {
    try {
        const snap = await synczbase.query("SELECT * FROM services WHERE \"portfolioId\" = $1", [portfolioId]);
        const list = (snap || []).map((row: any) => {
            if (row.data) {
                try {
                    const parsed = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                    return { ...row, ...parsed, id: row.id || parsed.id };
                } catch (e) {}
            }
            return row;
        });
        
        if (list.length > 0) return list as Service[];
        
        // Fallback: search in JS memory if DB column structure was pre-migration or has variations
        const all = await getServices();
        return all.filter(s => s.portfolioId === portfolioId);
    } catch (e) {
        console.error("Failed to get services for portfolio", e);
        return [];
    }
};

export const createService = async (service: Service) => {
    const id = service.id || `service_${Date.now()}_${Math.random().toString(36).substring(2,9)}`;
    const payload = cleanServicePayload({ ...service, id }, false);
    
    await synczbase.query(
        `INSERT INTO services (
            id, "portfolioId", title, price, description, enabled, "isBookable", 
            "durationMinutes", "maxParticipants", "locationMode", "quoteFormType", data
         ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12)`,
        [
            id,
            payload.portfolioId || null,
            payload.title || '',
            payload.price || 0,
            payload.description || '',
            payload.enabled !== false,
            payload.isBookable === true,
            payload.durationMinutes || 0,
            payload.maxParticipants || 1,
            payload.locationMode || 'PHYSICAL',
            payload.quoteFormType || 'NONE',
            JSON.stringify(payload)
        ]
    );
};

export const updateService = async (id: string, data: Partial<Service>) => {
    try {
        const snap = await synczbase.query("SELECT * FROM services WHERE id = $1 LIMIT 1", [id]);
        if (snap && snap.length > 0) {
            const row = snap[0];
            let existing: any = {};
            if (row.data) {
                try {
                    existing = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
                } catch (e) {}
            } else {
                existing = { ...row };
            }
            
            const updated = cleanServicePayload({ ...existing, ...data }, true);
            await synczbase.query(
                `UPDATE services SET 
                    "portfolioId" = $1, title = $2, price = $3, description = $4, enabled = $5, 
                    "isBookable" = $6, "durationMinutes" = $7, "maxParticipants" = $8, 
                    "locationMode" = $9, "quoteFormType" = $10, data = $11 
                 WHERE id = $12`,
                [
                    updated.portfolioId || null,
                    updated.title || '',
                    updated.price || 0,
                    updated.description || '',
                    updated.enabled !== false,
                    updated.isBookable === true,
                    updated.durationMinutes || 0,
                    updated.maxParticipants || 1,
                    updated.locationMode || 'PHYSICAL',
                    updated.quoteFormType || 'NONE',
                    JSON.stringify(updated),
                    id
                ]
            );
        }
    } catch (e) {
        console.error("Failed to update service", e);
    }
};

export const deleteService = async (id: string) => {
    await synczbase.query("DELETE FROM services WHERE id = $1", [id]);
};

export const seedDefaultServices = async (targetPortfolioId?: string) => {
    let ownerId = targetPortfolioId || 'system';
    if (!targetPortfolioId) {
        try {
            const user = await synczbase.apiCall('/api/auth/me', 'GET');
            ownerId = user?.id || user?.uid || 'system';
        } catch(e) {}
    }

    const services = [
        { 
            title: 'Recording Session (with Sound Engineer)', 
            serviceType: 'Project',
            price: 75, 
            description: 'Full-service studio recording session with a professional resident sound engineer. Fully tuned vocal booths, premium microphone lockers, and instant digital stems delivery.', 
            enabled: true,
            isBookable: true,
            portfolioId: ownerId,
            durationMinutes: 60,
            locationMode: 'PHYSICAL',
            maxParticipants: 5,
            features: ['Professional Engineer Included', 'Microphone Locker Access', 'Digital Stems Delivered', 'Vocal Tuning Ready']
        },
        { 
            title: 'Premium Mixing & Stereo Mastering', 
            serviceType: 'Custom',
            price: 200, 
            description: 'Industry-standard mixing and stereo mastering for your tracks. Analog summing, precise vocal compression, de-essing, surgical EQ, and maximum loudness optimization for streaming platforms.', 
            enabled: true,
            isBookable: false,
            portfolioId: ownerId,
            durationMinutes: 0,
            locationMode: 'REMOTE',
            quoteFormType: 'BASIC',
            features: ['Stereo WAV & MP3 Delivery', '2 Revision Rounds Included', 'Streaming Platform Optimization', 'Analog Outboard Processing']
        },
        { 
            title: 'Dolby Atmos Spatial Audio Mix', 
            serviceType: 'Custom',
            price: 450, 
            description: 'Transform your stereophonic records into fully immersive spatial audio soundscapes. Certified mixing for Apple Music, Tidal, and Amazon Music Spatial distribution.', 
            enabled: true,
            isBookable: false,
            portfolioId: ownerId,
            durationMinutes: 0,
            locationMode: 'REMOTE',
            quoteFormType: 'BASIC',
            features: ['7.1.4 Dolby Atmos ADM BWF Deliverable', 'Apple Spatial Audio Binaural Pass', 'Compatible Multi-Channel Stems', 'Industry Certified Mix Engineer']
        },
        {
            title: '1-on-1 Production & Project Consultation',
            serviceType: 'Consultation',
            price: 120,
            description: 'Private 1-on-1 audio production masterclass or project review. Discuss your arrangement, sound selection, songwriting structure, and release planning with a seasoned veteran.',
            enabled: true,
            isBookable: true,
            durationMinutes: 60,
            portfolioId: ownerId,
            locationMode: 'REMOTE',
            maxParticipants: 1,
            features: ['Zoom Video Call Session', 'Live Screen & DAW Sharing', 'Arrangement & Beat Feedback', 'Release Strategy & PDF Guide']
        }
    ];
    
    for(const s of services) {
        // @ts-ignore
        await createService(s);
    }
};
